import { useEffect, useState, type RefObject } from 'react';
import { useParams } from 'react-router-dom';

const TRIAL_DURATION_MS = 10 * 60 * 1000;
const FADE_DURATION_MS = 1500;
const FADE_STEPS = 5;
const TRIAL_USED_KEY = 'spacemusic_trial_used';
const TRIAL_TOKEN: string | undefined = import.meta.env.VITE_TRIAL_STORE_TOKEN;

export interface TrialState {
  active: boolean;
  ended: boolean;
  alreadyUsed: boolean;
}

export function useTrialMode(audioRef: RefObject<HTMLAudioElement>, ready: boolean): TrialState {
  const { token } = useParams<{ token: string }>();
  const active = !!TRIAL_TOKEN && token === TRIAL_TOKEN;

  const [alreadyUsed] = useState(() => active && localStorage.getItem(TRIAL_USED_KEY) === 'true');
  const [ended, setEnded] = useState(alreadyUsed);

  useEffect(() => {
    if (!active || !ready) return;
    const audio = audioRef.current;
    if (!audio) return;

    // If trial has ended (either just now or on a prior visit), block any play attempt.
    if (ended) {
      const onPlayBlocked = () => audio.pause();
      audio.addEventListener('play', onPlayBlocked);
      return () => audio.removeEventListener('play', onPlayBlocked);
    }

    let timeoutId: number | null = null;
    let fadeId: number | null = null;
    let armed = false;

    const onPlay = () => {
      if (armed) return;
      armed = true;
      timeoutId = window.setTimeout(() => {
        const startVol = audio.volume;
        let step = 0;
        const stepMs = FADE_DURATION_MS / FADE_STEPS;
        fadeId = window.setInterval(() => {
          step += 1;
          audio.volume = startVol * (1 - step / FADE_STEPS);
          if (step >= FADE_STEPS) {
            if (fadeId !== null) {
              window.clearInterval(fadeId);
              fadeId = null;
            }
            audio.pause();
            audio.volume = startVol;
            localStorage.setItem(TRIAL_USED_KEY, 'true');
            setEnded(true);
          }
        }, stepMs);
      }, TRIAL_DURATION_MS);
    };

    audio.addEventListener('play', onPlay);
    return () => {
      audio.removeEventListener('play', onPlay);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      if (fadeId !== null) window.clearInterval(fadeId);
      // Note: clearing the timeout on unmount means navigating away and returning
      // resets the 10-minute window. This is intentional — strong re-trial prevention
      // (e.g. persisting elapsed time in localStorage) is out of scope for this feature.
    };
    // audioRef is a stable RefObject; its identity never changes, so it won't trigger
    // re-runs. It's listed here to satisfy react-hooks/exhaustive-deps.
  }, [active, ended, ready, audioRef]);

  return { active, ended, alreadyUsed };
}
